/**
 * ZIP без сжатия — упаковщик для xlsx.
 *
 * Книга Excel это zip с несколькими xml и картинками. Готовой библиотеки
 * в проекте нет: SheetJS картинки в ячейки не кладёт, а jszip тянется
 * внутрь docx и pptxgenjs, наружу не выходит. Своих тут полторы сотни
 * строк, зато они работают и в браузере, и в тестах без единой зависимости.
 *
 * Сжатия нет намеренно (метод «store»): тяжёлое в книге — скриншоты, а
 * они уже JPEG, и сжимать их второй раз бессмысленно. Excel такие файлы
 * читает, это часть формата.
 */

export type ZipEntry = {
  /** Путь внутри архива: «xl/worksheets/sheet1.xml». */
  path: string;
  data: Uint8Array;
};

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let i = 0; i < 256; i += 1) {
    let value = i;
    for (let bit = 0; bit < 8; bit += 1) {
      value = value & 1 ? 0xedb88320 ^ (value >>> 1) : value >>> 1;
    }
    table[i] = value >>> 0;
  }
  return table;
})();

export function crc32(data: Uint8Array): number {
  let crc = 0xffffffff;
  for (let i = 0; i < data.length; i += 1) {
    crc = CRC_TABLE[(crc ^ data[i]) & 0xff] ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
}

/*
 * Дата у всех файлов одна и та же — 1 января 1980 года, самая ранняя,
 * какую знает формат. Так две сборки одной и той же таблицы дают
 * побайтово одинаковый файл, и это можно проверить тестом.
 */
const DOS_TIME = 0;
const DOS_DATE = 33;

const LOCAL_HEADER = 30;
const CENTRAL_HEADER = 46;
const END_RECORD = 22;

/** Готовый архив. Тип с ArrayBuffer — чтобы байты принимал Blob в браузере. */
export function zipStore(entries: ZipEntry[]): Uint8Array<ArrayBuffer> {
  const encoder = new TextEncoder();
  const names = entries.map((entry) => encoder.encode(entry.path));
  const size =
    entries.reduce((acc, entry, i) => acc + LOCAL_HEADER + names[i].length + entry.data.length, 0) +
    entries.reduce((acc, _, i) => acc + CENTRAL_HEADER + names[i].length, 0) +
    END_RECORD;

  const out = new Uint8Array(size);
  const view = new DataView(out.buffer);
  const offsets: number[] = [];
  const sums: number[] = [];
  let at = 0;

  entries.forEach((entry, i) => {
    const name = names[i];
    const sum = crc32(entry.data);
    offsets.push(at);
    sums.push(sum);

    view.setUint32(at, 0x04034b50, true);
    view.setUint16(at + 4, 20, true);
    // Бит 11 — имена в UTF-8. У нас они латиницей, но так честнее.
    view.setUint16(at + 6, 0x0800, true);
    view.setUint16(at + 8, 0, true);
    view.setUint16(at + 10, DOS_TIME, true);
    view.setUint16(at + 12, DOS_DATE, true);
    view.setUint32(at + 14, sum, true);
    view.setUint32(at + 18, entry.data.length, true);
    view.setUint32(at + 22, entry.data.length, true);
    view.setUint16(at + 26, name.length, true);
    view.setUint16(at + 28, 0, true);
    out.set(name, at + LOCAL_HEADER);
    out.set(entry.data, at + LOCAL_HEADER + name.length);
    at += LOCAL_HEADER + name.length + entry.data.length;
  });

  const directoryAt = at;
  entries.forEach((entry, i) => {
    const name = names[i];
    view.setUint32(at, 0x02014b50, true);
    view.setUint16(at + 4, 20, true);
    view.setUint16(at + 6, 20, true);
    view.setUint16(at + 8, 0x0800, true);
    view.setUint16(at + 10, 0, true);
    view.setUint16(at + 12, DOS_TIME, true);
    view.setUint16(at + 14, DOS_DATE, true);
    view.setUint32(at + 16, sums[i], true);
    view.setUint32(at + 20, entry.data.length, true);
    view.setUint32(at + 24, entry.data.length, true);
    view.setUint16(at + 28, name.length, true);
    view.setUint16(at + 30, 0, true);
    view.setUint16(at + 32, 0, true);
    view.setUint16(at + 34, 0, true);
    view.setUint16(at + 36, 0, true);
    view.setUint32(at + 38, 0, true);
    view.setUint32(at + 42, offsets[i], true);
    out.set(name, at + CENTRAL_HEADER);
    at += CENTRAL_HEADER + name.length;
  });

  view.setUint32(at, 0x06054b50, true);
  view.setUint16(at + 4, 0, true);
  view.setUint16(at + 6, 0, true);
  view.setUint16(at + 8, entries.length, true);
  view.setUint16(at + 10, entries.length, true);
  view.setUint32(at + 12, at - directoryAt, true);
  view.setUint32(at + 16, directoryAt, true);
  view.setUint16(at + 20, 0, true);

  return out;
}
